import axios from 'axios';
import { openBillingUpsell } from '../utils/billingUpsell.js';
import { getBillingGeoHeaders, isBillingGeoRequest } from './billingGeoHeaders.js';

const API_URL = import.meta.env.VITE_API_URL;
const GEO_LOOKUP_TIMEOUT_MS = 1500;

// On module load, trigger background IP geolocation to resolve region based on public IP addresses.
let detectedCountryCode = 'IN'; // default fallback for India region
let detectedPriceRegion = 'IN';
let ipDetectionPromise = null;

async function detectPublicIpCountry() {
  try {
    // In development mode (npm run dev), bypass localStorage cache to allow seamless VPN testing
    const isDev = import.meta.env.DEV;
    const cachedCountry = isDev ? null : localStorage.getItem('tasklina_ip_country');
    
    if (cachedCountry) {
      detectedCountryCode = cachedCountry;
      detectedPriceRegion = cachedCountry === 'IN' ? 'IN' : 'GLOBAL';
      return { countryCode: detectedCountryCode, priceRegion: detectedPriceRegion };
    }

    const controller = new AbortController();
    const timeout = globalThis.setTimeout(() => controller.abort(), GEO_LOOKUP_TIMEOUT_MS);
    let data = null;
    try {
      const res = await fetch('https://api.db-ip.com/v2/free/self', { signal: controller.signal });
      data = await res.json();
    } finally {
      globalThis.clearTimeout(timeout);
    }
    if (data && data.countryCode) {
      const cc = String(data.countryCode).toUpperCase();
      detectedCountryCode = cc;
      detectedPriceRegion = cc === 'IN' ? 'IN' : 'GLOBAL';
      if (!isDev) {
        localStorage.setItem('tasklina_ip_country', cc);
      }
    }
  } catch (e) {
    // Fail gracefully back to defaults (IN region)
  }
  return { countryCode: detectedCountryCode, priceRegion: detectedPriceRegion };
}

function getIpDetectionPromise() {
  if (!ipDetectionPromise) {
    ipDetectionPromise = detectPublicIpCountry();
  }
  return ipDetectionPromise;
}

// Start detection immediately on module load
getIpDetectionPromise();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Attach helper methods to axios singleton so other views can inspect current state
api.getDetectedRegion = () => detectedPriceRegion;
api.getDetectedCountryCode = () => detectedCountryCode;
api.ensureDetectedRegion = getIpDetectionPromise;

function shouldOpenBillingUpsell(config) {
  if (config?.showBillingUpsell === true) return true;
  if (config?.showBillingUpsell === false) return false;

  const method = String(config?.method || 'get').toLowerCase();
  return method !== 'get';
}

// Add token to requests if available
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const geo = isBillingGeoRequest(config?.url)
      ? await getIpDetectionPromise()
      : { countryCode: detectedCountryCode, priceRegion: detectedPriceRegion };

    Object.assign(config.headers, getBillingGeoHeaders({
      url: config.url,
      geo,
      isDevelopment: import.meta.env.DEV,
    }));

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized - but only for protected routes
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      // Don't redirect if already on auth pages or if it's a login/register failure
      if (!currentPath.includes('/auth/')) {
        localStorage.removeItem('token');
        window.location.href = '/auth/login';
      }
    }
    
    const errorData = error.response?.data || {};
    const code = errorData.code;
    const billingCodes = new Set([
      'PLAN_REQUIRED',
      'FEATURE_UNAVAILABLE',
      'LIMIT_REACHED',
      'USAGE_LIMIT_REACHED',
      'BILLING_FEATURE_UNCONFIGURED',
      'BILLING_LIMIT_UNCONFIGURED',
      'BILLING_LIMIT_UNAVAILABLE',
      'BILLING_UNAVAILABLE',
      'FILE_SIZE_LIMIT_REACHED',
      'STORAGE_LIMIT_REACHED',
      'SEAT_LIMIT_REACHED',
      'EMPLOYEE_LIMIT_REACHED',
      'DEPARTMENT_LIMIT_REACHED'
    ]);
    const upsellCodes = new Set([
      'PLAN_REQUIRED',
      'FEATURE_UNAVAILABLE',
      'LIMIT_REACHED',
      'USAGE_LIMIT_REACHED',
      'FILE_SIZE_LIMIT_REACHED',
      'STORAGE_LIMIT_REACHED',
      'SEAT_LIMIT_REACHED',
      'EMPLOYEE_LIMIT_REACHED',
      'DEPARTMENT_LIMIT_REACHED'
    ]);

    if (billingCodes.has(code)) {
      error.isBillingLimit = true;
      error.billingCode = code;
      error.billingMessage = errorData.error || errorData.message || 'Upgrade your plan to access this feature';
      error.billingMeta = {
        ...(errorData.meta || {}),
        itemId: errorData.itemId,
        limit: errorData.limit,
        current: errorData.current ?? errorData.used,
        used: errorData.used,
        resetAt: errorData.resetAt,
        workspaceType: errorData.workspaceType,
        upgradeUrl: errorData.upgradeUrl
      };

      if (upsellCodes.has(code) && shouldOpenBillingUpsell(error.config)) {
        openBillingUpsell({
          code,
          message: error.billingMessage,
          ...error.billingMeta
        });
      }
    }

    // Handle 403 Forbidden for finance routes - these are expected for non-finance users
    // Silently handle permission errors on finance endpoints
    if (error.response?.status === 403) {
      const requestUrl = error.config?.url || '';

      if (requestUrl.includes('/org/finance/')) {
        // Add flag to identify this as a permission error (not a critical error)
        error.isPermissionError = true;
        error.silentError = true;
      }
    }
    
    // Handle 429 Rate Limit - Add specific error information
    if (error.response?.status === 429) {
      // Enhance error object with rate limit info
      error.isRateLimit = true;
      error.rateLimitMessage = errorData?.error || 'Too many requests. Please try again later';
      error.rateLimitCode = code;
    }
    
    return Promise.reject(error);
  }
);

export default api;
